import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from '@/hooks/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LeadWithRelations } from '@/types';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { DEFAULT_COMPANY_LOCALE, isDateOnlyPast, isValidPostcode, type CompanyLocale } from '@/utils/locale';
import { normalizePhoneE164 } from '@/utils/phone';
import { useTechsByMetro, useTechsWithMetros, useMetroFromPostcode, useServiceTypes } from '@/hooks/useReferenceData';
import { useConvertLeadV2 } from '@/hooks/useLeads';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { AddressVerificationBadge } from '@/components/ui/AddressVerificationBadge';
import type { NormalizedAddress } from '@/utils/parseGoogleAddress';
import { geocodeAddress } from '@/utils/geocodeAddress';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, ArrowRight, CheckCircle2, Briefcase, User, Phone, Mail, MapPin, DollarSign, Calendar, Clock, FileText, Tag, Sun, Sunset, Moon, CalendarClock } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConversionSuccessDialog } from './ConversionSuccessDialog';

const createConvertSchema = (locale: CompanyLocale = DEFAULT_COMPANY_LOCALE) => z.object({
  tech_id: z.string().min(1, 'Please select a technician'),
  customer_name: z.string().min(1, 'Customer name is required'),
  customer_phone: z.string().min(1, 'Phone is required'),
  customer_email: z.string().optional(),
  customer_address: z.string().min(1, 'Street address is required'),
  customer_suburb: z.string().optional(),
  customer_state: z.string().optional(),
  customer_postcode: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine((v) => isValidPostcode(v, locale), locale.postcode.message),
  issue_description: z.string().min(1, 'Issue description is required'),
  service_type_id: z.string().min(1, 'Please select a service type'),
  quoted_price: z.string().optional(),
  scheduled_date: z.string().optional(),
  scheduled_time: z.string().optional(),
  scheduled_time_start: z.string().optional(),
  scheduled_time_end: z.string().optional(),
  notes: z.string().optional(),
  is_recall: z.boolean().optional(),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
}).refine((data) => {
  // If time_start is set, time_end must also be set
  if (data.scheduled_time_start && !data.scheduled_time_end) {
    return false;
  }
  if (data.scheduled_time_end && !data.scheduled_time_start) {
    return false;
  }
  // If both are set, end must be after start
  if (data.scheduled_time_start && data.scheduled_time_end) {
    return data.scheduled_time_end > data.scheduled_time_start;
  }
  return true;
}, {
  message: 'End time must be after start time',
  path: ['scheduled_time_end'],
});

type ConvertFormData = z.infer<ReturnType<typeof createConvertSchema>>;

interface ConvertToJobWizardProps {
  lead: LeadWithRelations;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ConvertToJobWizard({ lead, open, onOpenChange }: ConvertToJobWizardProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdJob, setCreatedJob] = useState<{ id: string; number: string } | null>(null);
  
  const { data: serviceTypes = [] } = useServiceTypes();
  const convertLead = useConvertLeadV2();
  const { formatDateInput, parseDate, timezone } = useTimezoneUtils();
  const { locale } = useCompanyLocale();
  const convertSchema = useMemo(() => createConvertSchema(locale), [locale]);

  const form = useForm<ConvertFormData>({
    resolver: zodResolver(convertSchema),
    defaultValues: {
      tech_id: lead.assigned_tech_id || '',
      customer_name: lead.customer_name,
      customer_phone: lead.customer_phone,
      customer_email: lead.customer_email || '',
      customer_address: lead.customer_address || '',
      customer_suburb: (lead as any).customer_suburb || '',
      customer_state: (lead as any).customer_state || '',
      customer_postcode: (lead as any).customer_postcode || '',
      issue_description: lead.issue_description,
      service_type_id: lead.service_type_id || '',
      quoted_price: lead.quoted_price?.toString() || '',
      scheduled_date: formatDateInput(new Date()), // Always default to today in configured timezone
      scheduled_time: lead.scheduled_time || '',
      scheduled_time_start: lead.scheduled_time_start || '',
      scheduled_time_end: lead.scheduled_time_end || '',
      notes: lead.notes || '',
      is_recall: false,
    },
  });

  const watchedPostcode = form.watch('customer_postcode');
  const { data: postcodeMetroId } = useMetroFromPostcode(watchedPostcode);
  const effectiveMetroId = postcodeMetroId ?? lead.metro_id;
  const { data: techs = [] } = useTechsByMetro(effectiveMetroId);
  const { data: allTechs = [] } = useTechsWithMetros(true);

  // Quick time preset functions
  const setTimePreset = (start: string, end: string) => {
    form.setValue('scheduled_time_start', start);
    form.setValue('scheduled_time_end', end);
  };

  // Validation warnings (service type now required, removed from warnings)
  const getValidationWarnings = () => {
    const warnings: string[] = [];
    if (!form.watch('quoted_price')) warnings.push('No quoted price set');
    if (!form.watch('scheduled_date')) warnings.push('No scheduled date');
    else if (isDateOnlyPast(form.watch('scheduled_date'), timezone)) warnings.push('Scheduled date is in the past');
    if (!form.watch('customer_email')) warnings.push('No email for customer communication');
    return warnings;
  };

  const warnings = getValidationWarnings();
  const jobNumber = lead.lead_number.replace('LEAD-', 'JOB-');

  // Everything the schema checks that lives on step 1. Step 2 only shows the
  // technician, so a step-1 error must be surfaced before leaving step 1, or
  // "Book Job" would fail with nothing visible to the CSR.
  const STEP_ONE_FIELDS: (keyof ConvertFormData)[] = [
    'customer_name', 'customer_phone', 'customer_address', 'customer_postcode',
    'issue_description', 'service_type_id', 'scheduled_time_start', 'scheduled_time_end',
  ];

  const handleNext = async () => {
    const ok = await form.trigger(STEP_ONE_FIELDS, { shouldFocus: true });
    if (!ok) {
      toast({
        title: 'Please fix the highlighted fields',
        description: Object.values(form.formState.errors).map(e => e?.message).filter(Boolean).join('. '),
        variant: 'destructive',
      });
      return;
    }
    setStep(2);
  };

  // Validation failed on "Book Job": show the step that owns the first error.
  const handleInvalid = (errors: Record<string, { message?: string } | undefined>) => {
    const keys = Object.keys(errors);
    if (keys.some(k => STEP_ONE_FIELDS.includes(k as keyof ConvertFormData))) setStep(1);
    toast({
      title: 'Please fix the highlighted fields',
      description: keys.map(k => errors[k]?.message).filter(Boolean).join('. '),
      variant: 'destructive',
    });
  };

  const handleBack = () => {
    setStep(1);
  };

  // Called when user selects a Google Places suggestion.
  // isPartial guard: only set split fields when the value is non-empty.
  const handleAddressSelected = (normalized: NormalizedAddress) => {
    const setIfChanged = (name: keyof ConvertFormData, newVal: string) => {
      if (form.getValues(name) !== newVal) {
        form.setValue(name, newVal as never, { shouldDirty: true });
      }
    };
    setIfChanged('customer_address', normalized.street);
    if (normalized.suburb)   setIfChanged('customer_suburb', normalized.suburb);
    if (normalized.state)    setIfChanged('customer_state', normalized.state);
    if (normalized.postcode) setIfChanged('customer_postcode', normalized.postcode);

    // Coordinates: silent, no dirty flag
    if (normalized.lat != null) form.setValue('lat', normalized.lat, { shouldDirty: false });
    if (normalized.lng != null) form.setValue('lng', normalized.lng, { shouldDirty: false });
  };

  const handleSubmit = async (data: ConvertFormData) => {
    if (convertLead.isPending) return;

    // If the CSR typed an address without picking from autocomplete, geocode it now
    // so the job lands in the right metro from the start.
    let lat = data.lat ?? null;
    let lng = data.lng ?? null;
    if (data.customer_address && (lat == null || lng == null)) {
      const coords = await geocodeAddress(data.customer_address, locale.countryCode);
      if (coords) {
        lat = coords.lat;
        lng = coords.lng;
      }
      // If geocoding fails, the DB trigger safety net will pick it up asynchronously
    }

    const overrideData = {
      customer_name: data.customer_name,
      customer_phone: normalizePhoneE164(data.customer_phone, locale),
      customer_email: data.customer_email || null,
      customer_address: data.customer_address,
      customer_suburb: data.customer_suburb || null,
      customer_state: data.customer_state || null,
      customer_postcode: data.customer_postcode || null,
      issue_description: data.issue_description,
      service_type_id: data.service_type_id || null,
      quoted_price: data.quoted_price ? parseFloat(data.quoted_price) : null,
      // Midnight in the company timezone. A bare "yyyy-MM-dd" is cast by the RPC as midnight
      // UTC, which is the previous evening anywhere west of UTC (US tenants saw the day before).
      scheduled_date: data.scheduled_date ? parseDate(data.scheduled_date).toISOString() : null,
      scheduled_time: data.scheduled_time || null,
      scheduled_time_start: data.scheduled_time_start || null,
      scheduled_time_end: data.scheduled_time_end || null,
      notes: data.notes || null,
      lat,
      lng,
    };

    convertLead.mutate(
      {
        leadId: lead.id,
        techId: data.tech_id,
        isRecall: data.is_recall === true,
        overrideData
      },
      {
        onSuccess: (data) => {
          const result = Array.isArray(data) ? data[0] : data;
          setCreatedJob({ id: result?.job_id || '', number: result?.job_number || '' });
          onOpenChange(false);
          setShowSuccessDialog(true);
          setStep(1);
          form.reset();
        },
      }
    );
  };

  const handleCloseSuccess = () => {
    setShowSuccessDialog(false);
    setCreatedJob(null);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-primary" />
              Book Job
              <Badge variant="outline" className="ml-2">Step {step} of 2</Badge>
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit, handleInvalid)} className="space-y-6">
              {/* Step 1: Review & Edit */}
              {step === 1 && (
                <div className="space-y-6">
                  {/* Job Number Preview */}
                  <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Converting</p>
                          <p className="text-lg font-semibold">{lead.lead_number}</p>
                        </div>
                        <ArrowRight className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Will become</p>
                          <p className="text-lg font-semibold text-primary">{jobNumber}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Warnings */}
                  {warnings.length > 0 && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <p className="font-medium mb-2">Missing recommended information:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                          {warnings.map((warning, idx) => (
                            <li key={idx}>{warning}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Scheduling Section - Priority for Dispatchers */}
                  <div className="space-y-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2">
                      <CalendarClock className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold text-lg">Scheduling</h3>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="scheduled_date"
                        render={({ field }) => (
                          <FormItem className="col-span-3 sm:col-span-1">
                            <FormLabel>Scheduled Date *</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="scheduled_time_start"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Time Window Start</FormLabel>
                            <FormControl>
                              <Input type="time" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="scheduled_time_end"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Time Window End</FormLabel>
                            <FormControl>
                              <Input type="time" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Quick Time Presets:</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setTimePreset('08:00', '12:00')}
                          className="flex items-center gap-1.5"
                        >
                          <Sun className="h-3.5 w-3.5" />
                          Morning
                          <span className="text-xs text-muted-foreground ml-1">8-12</span>
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setTimePreset('12:00', '16:00')}
                          className="flex items-center gap-1.5"
                        >
                          <Sun className="h-3.5 w-3.5" />
                          Afternoon
                          <span className="text-xs text-muted-foreground ml-1">12-4</span>
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setTimePreset('16:00', '20:00')}
                          className="flex items-center gap-1.5"
                        >
                          <Sunset className="h-3.5 w-3.5" />
                          Evening
                          <span className="text-xs text-muted-foreground ml-1">4-8</span>
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setTimePreset('08:00', '18:00')}
                          className="flex items-center gap-1.5"
                        >
                          <Clock className="h-3.5 w-3.5" />
                          All Day
                          <span className="text-xs text-muted-foreground ml-1">8-6</span>
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Customer Information */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-4">
                        <User className="h-4 w-4 text-primary" />
                        <h3 className="font-semibold">Customer Information</h3>
                      </div>

                      <FormField
                        control={form.control}
                        name="customer_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Customer Name *</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="customer_phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone *</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="customer_email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input type="email" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="customer_address"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Street Address *</FormLabel>
                            <FormControl>
                              <AddressAutocomplete
                                id="wizard-customer-address"
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onAddressSelected={handleAddressSelected}
                                disabled={field.disabled}
                                placeholder="Start typing a street address…"
                              />
                            </FormControl>
                            <AddressVerificationBadge
                              address={field.value}
                              lat={form.watch('lat')}
                              lng={form.watch('lng')}
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-3 gap-3">
                        <FormField
                          control={form.control}
                          name="customer_suburb"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{locale.addressLabels.locality}</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder={locale.addressPlaceholders.locality} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="customer_state"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{locale.addressLabels.region}</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder={`e.g., ${locale.addressPlaceholders.region}`} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="customer_postcode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{locale.addressLabels.postcode}</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder={locale.postcode.placeholder} maxLength={locale.postcode.maxLength} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* Job Details */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-4">
                        <FileText className="h-4 w-4 text-primary" />
                        <h3 className="font-semibold">Job Details</h3>
                      </div>

                      <FormField
                        control={form.control}
                        name="service_type_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Service Type</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select service type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {serviceTypes.map((type) => (
                                  <SelectItem key={type.id} value={type.id}>
                                    {type.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {serviceTypes.length === 0 && (
                              <p className="text-xs text-destructive">
                                No service types yet, and one is required to book a job.{' '}
                                <Link to="/settings?tab=service-types" className="underline" onClick={() => onOpenChange(false)}>
                                  Add service types in Settings
                                </Link>
                              </p>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="issue_description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Issue Description *</FormLabel>
                            <FormControl>
                              <Textarea {...field} rows={3} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="quoted_price"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Quoted Price</FormLabel>
                            <FormControl>
                              <Input type="number" step="0.01" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Notes</FormLabel>
                            <FormControl>
                              <Textarea {...field} rows={3} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="is_recall"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value ?? false}
                                onCheckedChange={(checked) => field.onChange(checked === true)}
                              />
                            </FormControl>
                            <FormLabel className="cursor-pointer font-normal">
                              Recall (warranty / return visit)
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                      Cancel
                    </Button>
                    <Button type="button" onClick={handleNext}>
                      Next: Assign Technician
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Assign Technician */}
              {step === 2 && (
                <div className="space-y-6">
                  <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="h-8 w-8 text-success" />
                        <div>
                          <h3 className="font-semibold">Ready to Convert</h3>
                          <p className="text-sm text-muted-foreground">
                            {lead.lead_number} → {jobNumber}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <FormField
                    control={form.control}
                    name="tech_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Assign Technician *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select technician" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {techs.map((tech) => (
                              <SelectItem key={tech.id} value={tech.id}>
                                {tech.name}
                              </SelectItem>
                            ))}
                            {/* Nobody covers this lead's metro: offer every active tech rather than an empty list */}
                            {techs.length === 0 && allTechs.map((tech) => (
                              <SelectItem key={tech.id} value={tech.id} className="text-warning">
                                {tech.name} (outside metro)
                              </SelectItem>
                            ))}
                            {/* Show pre-assigned tech if filtered out by metro */}
                            {techs.length > 0 && lead.assigned_tech_id && !techs.find(t => t.id === lead.assigned_tech_id) && (
                              <SelectItem value={lead.assigned_tech_id} className="text-warning">
                                {lead.tech_profile?.name ?? 'Current tech'} (outside metro)
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        {techs.length === 0 && allTechs.length > 0 && (
                          <p className="text-xs text-warning">
                            No technician is assigned to this lead's area. Everyone is listed; pick one, or add the area to a technician in Settings.
                          </p>
                        )}
                        {allTechs.length === 0 && (
                          <p className="text-xs text-destructive">
                            No active technicians yet. Add one in Settings before booking a job.
                          </p>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <p className="font-medium mb-2">What will happen:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Job status will be set based on scheduled date (today = in progress, future = scheduled)</li>
                        <li>Lead status will change to "Converted"</li>
                        <li>Customer record will be created/updated</li>
                        <li>All lead information will be copied to the job</li>
                      </ul>
                    </AlertDescription>
                  </Alert>

                  <div className="flex justify-between gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={handleBack}>
                      Back
                    </Button>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={convertLead.isPending || form.formState.isSubmitting}
                      >
                        {convertLead.isPending || form.formState.isSubmitting
                          ? 'Booking...'
                          : 'Book Job'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {createdJob && (
        <ConversionSuccessDialog
          open={showSuccessDialog}
          onOpenChange={handleCloseSuccess}
          jobId={createdJob.id}
          jobNumber={createdJob.number}
        />
      )}
    </>
  );
}
